import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopNav from "@/components/TopNav";
import { api } from "@/lib/api";
import { LOBBY } from "@/constants/testIds";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function Lobby() {
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [botCount, setBotCount] = useState(0);
  const [modes, setModes] = useState([]);
  const [mode, setMode] = useState("classic");
  const [rules, setRules] = useState({ stack_draws: true, draw_to_match: false, force_play: false });

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/modes");
        setModes(r.data.modes);
        setMode(r.data.default);
      } catch (e) {}
    })();
  }, []);

  const authHeader = () => {
    const tok = localStorage.getItem("session_token");
    return tok ? { Authorization: `Bearer ${tok}` } : {};
  };

  const isUno = modes.find(m => m.id === mode)?.game_type !== "skipbo";

  const createRoom = async () => {
    setBusy(true);
    try {
      const r = await api.post("/rooms/create", { is_private: true, max_players: 4, add_bots: botCount, mode, rules: isUno ? rules : {} }, { headers: authHeader() });
      nav(`/game/${r.data.room_id}`);
    } catch (e) {
      toast.error("Could not create room");
    } finally { setBusy(false); }
  };

  const joinRoom = async () => {
    if (!code.trim()) { toast.error("Enter a room code"); return; }
    setBusy(true);
    try {
      const r = await api.post("/rooms/join", { code }, { headers: authHeader() });
      nav(`/game/${r.data.room_id}`);
    } catch (e) {
      toast.error("Room not found");
    } finally { setBusy(false); }
  };

  const quickMatch = async () => {
    setBusy(true);
    try {
      const r = await api.post("/rooms/quickmatch", { mode, rules: isUno ? rules : {} }, { headers: authHeader() });
      nav(`/game/${r.data.room_id}`);
    } catch (e) {
      toast.error("Quickmatch failed");
    } finally { setBusy(false); }
  };

  const selectedMode = modes.find(m => m.id === mode);
  const toggleRule = (k) => setRules(r => ({ ...r, [k]: !r[k] }));

  return (
    <div className="min-h-screen relative" data-testid={LOBBY.container}>
      <TopNav/>
      <main className="max-w-6xl mx-auto px-4 sm:px-10 py-6 sm:py-10 relative z-10">
        <div className="mb-6 sm:mb-10">
          <div className="text-xs uppercase tracking-[0.3em] text-white/50 mb-2">Lobby</div>
          <h1 className="font-display text-3xl sm:text-5xl tracking-tighter">Pick your poison.</h1>
        </div>

        {/* Mode selector */}
        <div className="mb-6 rounded-2xl bg-[#161324] border border-white/5 p-4 sm:p-6">
          <div className="text-xs uppercase tracking-[0.3em] text-white/50 mb-3">Game mode</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {modes.map(m => (
              <button
                key={m.id}
                data-testid={`${LOBBY.modeBtnPrefix}${m.id}`}
                onClick={() => setMode(m.id)}
                className={`text-left rounded-xl p-3 sm:p-4 border transition-all ${mode === m.id ? "border-[color:var(--uno-yellow)] bg-white/5" : "border-white/10 hover:border-white/20 bg-black/20"}`}
              >
                <div className="flex items-center gap-2">
                  <span className={`inline-block w-2.5 h-2.5 rounded-full ${m.game_type === "skipbo" ? "bg-[color:var(--uno-blue)]" : "bg-[color:var(--uno-red)]"}`}/>
                  <div className="font-display text-sm sm:text-base">{m.label}</div>
                </div>
                <div className="text-[11px] text-white/60 mt-1 leading-snug">{m.description}</div>
              </button>
            ))}
          </div>

          {/* Rules toggles (only for Uno modes) */}
          {isUno && (
            <div className="mt-5 pt-5 border-t border-white/5">
              <div className="text-xs uppercase tracking-[0.3em] text-white/50 mb-3">House rules</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {[
                  { key: "stack_draws", label: "Stack draws", hint: "Chain +2 on +2, +4 on +4" },
                  { key: "draw_to_match", label: "Draw until match", hint: "Keep drawing until you can play" },
                  { key: "force_play", label: "Force play", hint: "Must play if you have a playable card" },
                ].map(r => (
                  <button
                    key={r.key}
                    data-testid={`lobby-rule-${r.key}`}
                    onClick={() => toggleRule(r.key)}
                    className={`text-left rounded-xl p-3 border transition-all ${rules[r.key] ? "border-[color:var(--uno-green)] bg-[color:var(--uno-green)]/10" : "border-white/10 bg-black/20 hover:border-white/20"}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold">{r.label}</div>
                      <div className={`w-8 h-4 rounded-full relative transition-colors ${rules[r.key] ? "bg-[color:var(--uno-green)]" : "bg-white/10"}`}>
                        <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${rules[r.key] ? "left-4" : "left-0.5"}`}/>
                      </div>
                    </div>
                    <div className="text-[11px] text-white/60 mt-1">{r.hint}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 sm:gap-5">
          <motion.div whileHover={{ y: -4 }} className="md:col-span-4 md:row-span-2 tracing rounded-2xl p-6 sm:p-8 bg-[#161324] border border-white/5 min-h-[220px] sm:min-h-[280px] flex flex-col justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-white/50">Host</div>
              <div className="font-display text-2xl sm:text-4xl mt-2 tracking-tight">Create private room</div>
              <p className="text-white/60 mt-2 sm:mt-3 max-w-md text-sm sm:text-base">
                Playing <span className="text-white font-semibold">{selectedMode?.label || mode}</span>. Get a 6-char invite code. Share with friends. Add bots to fill.
              </p>
              <div className="mt-5 flex items-center gap-2 sm:gap-3 flex-wrap">
                <span className="text-xs uppercase tracking-[0.2em] text-white/60">Bots</span>
                {[0,1,2,3].map(n => (
                  <button key={n} onClick={() => setBotCount(n)}
                    className={`w-9 h-9 rounded-full font-display text-sm ${botCount===n ? "bg-white text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`}>
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <button
              data-testid={LOBBY.createRoomBtn}
              onClick={createRoom}
              disabled={busy}
              className="mt-6 self-start inline-flex items-center gap-2 px-6 sm:px-7 py-3 sm:py-4 rounded-full bg-[color:var(--uno-red)] text-white font-semibold hover:brightness-110 transition-[filter,transform] active:scale-95"
            >
              Deal me in <span className="ml-1">→</span>
            </button>
          </motion.div>

          <motion.div whileHover={{ y: -2 }} className="md:col-span-2 rounded-2xl p-5 sm:p-6 bg-[#161324] border border-white/5">
            <div className="text-xs uppercase tracking-[0.3em] text-white/50">Join</div>
            <div className="font-display text-xl sm:text-2xl mt-2">Enter a code</div>
            <input
              data-testid={LOBBY.joinRoomInput}
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ABC123"
              maxLength={6}
              className="mt-4 w-full bg-black/40 text-white text-xl sm:text-2xl font-display tracking-[0.3em] sm:tracking-[0.4em] text-center py-3 rounded-xl border border-white/10 focus:border-[color:var(--uno-yellow)] outline-none"
            />
            <button
              data-testid={LOBBY.joinRoomBtn}
              onClick={joinRoom}
              disabled={busy || code.length < 4}
              className="mt-3 w-full py-3 rounded-full bg-[color:var(--uno-yellow)] text-black font-semibold hover:brightness-110 disabled:opacity-50 transition-[filter,transform] active:scale-95"
            >Join room</button>
          </motion.div>

          <motion.div whileHover={{ y: -2 }} className="md:col-span-2 rounded-2xl p-5 sm:p-6 bg-[#161324] border border-white/5">
            <div className="text-xs uppercase tracking-[0.3em] text-white/50">Instant</div>
            <div className="font-display text-xl sm:text-2xl mt-2">Quick match</div>
            <p className="text-white/60 text-sm mt-2">You + 3 bots. No waiting.</p>
            <button
              data-testid={LOBBY.quickMatchBtn}
              onClick={quickMatch}
              disabled={busy}
              className="mt-5 sm:mt-6 w-full py-3 rounded-full bg-[color:var(--uno-green)] text-black font-semibold hover:brightness-110 transition-[filter,transform] active:scale-95"
            >Start now</button>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
